import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileImage, Plus, RefreshCw, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { publicPipelineError } from "@/lib/pipeline";
import { useParentScope } from "@/context/ParentContext";
import { EmptyState, ErrorState, Loading, StatusBadge } from "@/components/States";
import { Button } from "@/components/ui/button";
import { PaginationControls } from "@/components/PaginationControls";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ContextualNav } from "@/components/ContextualNav";

const PAGE_SIZE = 12;

export default function ParentMaterial() {
  const { student } = useParentScope();
  const [packs, setPacks] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ title: "", subject_id: "", exam_id: "" });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    if (!student) return;
    setError("");
    try {
      const [p, s, e] = await Promise.all([
        api.get("/parent/study-packs", { params: { student_id: student.id, limit: PAGE_SIZE, offset } }),
        api.get("/parent/subjects", { params: { student_id: student.id, limit: 100 } }),
        api.get("/parent/exams", { params: { student_id: student.id, limit: 100 } }),
      ]);
      setPacks(p.data);
      setTotal(Number(p.headers["x-total-count"] || p.data.length));
      setSubjects(s.data);
      setExams(e.data);
    } catch (err) {
      setError(apiError(err, "No pudimos cargar el material."));
      setPacks([]);
    }
  }, [student, offset]);

  useEffect(() => {
    setOffset(0);
  }, [student?.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!packs?.some((p) => ["queued", "processing", "indexing", "uploaded"].includes(p.status))) return;
    const timer = setInterval(load, 8000);
    return () => clearInterval(timer);
  }, [packs, load]);

  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/parent/study-packs", {
        student_id: student.id,
        subject_id: form.subject_id,
        exam_id: form.exam_id || null,
        title: form.title,
      });
      toast.success("Study Pack creado. Ahora sube las fotos del cuaderno.");
      setForm({ title: "", subject_id: "", exam_id: "" });
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(apiError(err, "No pudimos crear el Study Pack."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pack) => {
    const confirmed = window.confirm(`¿Eliminar "${pack.title}" y todas sus páginas y ejercicios? Esta acción no se puede deshacer.`);
    if (!confirmed) return;
    try {
      await api.delete(`/parent/study-packs/${pack.id}`);
      toast.success("Material de estudio eliminado.");
      if (packs.length === 1 && offset > 0) setOffset(Math.max(0, offset - PAGE_SIZE));
      else await load();
    } catch (err) {
      toast.error(apiError(err, "No pudimos eliminar el material de estudio."));
    }
  };

  if (!student) return <EmptyState title="Selecciona un hijo" description="Crea primero un perfil de estudiante." />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!packs) return <Loading label="Cargando material..." />;

  return (
    <div className="space-y-6" data-testid="parent-material-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Material de estudio</h1>
          <p className="mt-1 text-sm text-slate-500">
            Un Study Pack agrupa las paginas del cuaderno de un examen o conjunto de temas.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="estudia-touch rounded-xl" data-testid="refresh-packs-btn" aria-label="Actualizar material">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => setShowForm((v) => !v)}
            disabled={subjects.length === 0}
            className="estudia-touch rounded-xl bg-blue-600 font-semibold hover:bg-blue-700"
            data-testid="toggle-pack-form-btn"
          >
            <Plus className="mr-2 h-4 w-4" /> Nuevo Study Pack
          </Button>
        </div>
      </div>

      {subjects.length === 0 && (
        <div className="estudia-card p-5 text-sm text-slate-600" data-testid="no-subjects-warning">
          Primero crea una materia en{" "}
          <Link to="/p/calendario" className="font-semibold text-blue-700">
            Examenes y materias
          </Link>
          .
        </div>
      )}

      {showForm && (
        <form onSubmit={create} className="estudia-card animate-rise space-y-4 p-5" data-testid="pack-create-form">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-1">
              <Label htmlFor="ptitle">Titulo</Label>
              <Input
                id="ptitle"
                required
                className="estudia-touch mt-1.5"
                placeholder="Incas y Mayas"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="pack-title-input"
              />
            </div>
            <div>
              <Label htmlFor="psubject">Materia</Label>
              <select
                id="psubject"
                required
                className="estudia-touch mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                value={form.subject_id}
                onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
                data-testid="pack-subject-select"
              >
                <option value="">Selecciona...</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="pexam">Examen (opcional)</Label>
              <select
                id="pexam"
                className="estudia-touch mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                value={form.exam_id}
                onChange={(e) => setForm({ ...form, exam_id: e.target.value })}
                data-testid="pack-exam-select"
              >
                <option value="">Sin examen</option>
                {exams
                  .filter((e) => !form.subject_id || e.subject_id === form.subject_id)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title} · {e.exam_date}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="estudia-touch rounded-xl bg-blue-600 font-bold hover:bg-blue-700"
            data-testid="pack-create-submit-btn"
          >
            {busy ? "Creando..." : "Crear Study Pack"}
          </Button>
        </form>
      )}

      {packs.length === 0 ? (
        <EmptyState
          title="Sin Study Packs"
          description="Crea uno y sube las fotos de las paginas del cuaderno relacionadas con el examen."
          testId="packs-empty"
        />
      ) : (
        <>
        <div className="grid gap-4 md:grid-cols-2">
          {packs.map((pack) => (
            <article key={pack.id} className="estudia-card relative p-5 transition-transform duration-300 hover:-translate-y-0.5" data-testid="pack-card">
              <Link to={`/p/material/${pack.id}`} className="block pr-12" aria-label={`Abrir ${pack.title}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate font-display text-lg font-bold text-slate-900">{pack.title}</h3>
                  <p className="text-sm text-slate-500">{pack.subject}</p>
                </div>
                <StatusBadge status={pack.status} testId="pack-status-badge" />
              </div>
              <div className="mt-4 flex items-center gap-5 text-sm text-slate-600">
                <span className="flex items-center gap-1.5">
                  <FileImage className="h-4 w-4 text-slate-400" /> {pack.documents} paginas
                </span>
                <span>{pack.questions} ejercicios</span>
              </div>
              {pack.status === "failed" && pack.last_error && (
                <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="pack-error-text">
                  {publicPipelineError(pack.last_error)}
                </p>
              )}
              {(pack.warnings || []).length > 0 && (
                <p className="mt-3 text-xs text-amber-700" data-testid="pack-warning-text">
                  {pack.warnings.length} aviso(s) para revisar
                </p>
              )}
              </Link>
              <button
                type="button"
                onClick={() => remove(pack)}
                className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-xl text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                aria-label={`Eliminar ${pack.title}`}
                data-testid={`delete-pack-${pack.id}`}
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </article>
          ))}
        </div>
        <PaginationControls offset={offset} limit={PAGE_SIZE} total={total} onChange={setOffset} label="materiales" />
        </>
      )}

      <ContextualNav
        title="Conecta el material con el objetivo"
        description="Las fechas dan prioridad al estudio y el asistente te ayuda a interpretar los resultados."
        items={[
          { to: "/p/calendario", icon: "calendar", label: "Revisar calendario", description: "Vincula materias y próximos exámenes." },
          { to: "/p/asistente", icon: "assistant", label: "Preguntar al asistente", description: "Consulta qué conviene reforzar." },
        ]}
      />
    </div>
  );
}
